import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Calendar, Clock, Phone, Mail, User, XCircle, CalendarClock, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatIt(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}

function todayIso() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}

export default function ClientAppuntamentoPage() {
  const { magicLink } = useParams();
  const [loading, setLoading] = useState(true);
  const [ap, setAp] = useState(null);
  const [error, setError] = useState(null);

  // Cancella
  const [showCancella, setShowCancella] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Riprogramma
  const [showRipro, setShowRipro] = useState(false);
  const [newDate, setNewDate] = useState(todayIso());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  const fetchAp = async () => {
    try {
      const r = await axios.get(`${API}/client/appuntamento/${magicLink}`);
      setAp(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Appuntamento non trovato");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAp();
    // eslint-disable-next-line
  }, [magicLink]);

  const fetchSlots = async (dateStr) => {
    setLoadingSlots(true);
    try {
      const r = await axios.get(`${API}/client/appuntamento/${magicLink}/slots?data_str=${dateStr}`);
      if (r.data.closed) {
        setSlots([]);
      } else {
        setSlots((r.data.slots || []).filter((s) => s.libero));
      }
    } catch (e) {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (showRipro && newDate) fetchSlots(newDate);
    // eslint-disable-next-line
  }, [showRipro, newDate]);

  const handleCancella = async () => {
    if (!motivo.trim()) {
      toast.error("Inserisci il motivo della cancellazione");
      return;
    }
    setCancelling(true);
    try {
      await axios.post(`${API}/client/appuntamento/${magicLink}/cancella`, { motivo: motivo.trim() });
      toast.success("Appuntamento cancellato. Ti abbiamo inviato una conferma via email.");
      setShowCancella(false);
      fetchAp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione");
    } finally {
      setCancelling(false);
    }
  };

  const handleRiprogramma = async () => {
    if (!selectedSlot) {
      toast.error("Seleziona un orario");
      return;
    }
    setRescheduling(true);
    try {
      await axios.post(`${API}/client/appuntamento/${magicLink}/riprogramma`, {
        data: newDate,
        orario: selectedSlot,
      });
      toast.success(`Appuntamento spostato al ${formatIt(newDate)} alle ${selectedSlot}`);
      setShowRipro(false);
      setSelectedSlot("");
      fetchAp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore riprogrammazione");
    } finally {
      setRescheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !ap) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow border border-slate-200 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Appuntamento non trovato</h1>
          <p className="text-sm text-slate-600">{error || "Il link non è più valido."}</p>
        </div>
      </div>
    );
  }

  const isCancellato = ap.stato === "cancellato";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <Toaster richColors position="top-center" />
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className={`p-6 ${isCancellato ? "bg-red-600" : "bg-blue-600"} text-white`}>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <CalendarClock className="w-6 h-6" />
              {isCancellato ? "Appuntamento Cancellato" : "Il tuo Appuntamento"}
            </h1>
            <p className="text-sm text-blue-100 mt-1">Showroom Ingenio</p>
          </div>

          {/* Dettagli */}
          <div className="p-6 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-slate-500" />
              <span className="text-slate-700">
                <strong>{ap.nome} {ap.cognome}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-slate-500" />
              <span className="text-slate-700">{ap.telefono}</span>
            </div>
            {ap.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700">{ap.email}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-slate-700">
                <strong>{formatIt(ap.data)}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-slate-700">
                <strong>{ap.orario}</strong> (30 minuti)
              </span>
            </div>
            <div className="border-t border-slate-200 pt-3 mt-3">
              <p className="text-xs text-slate-500 mb-1">Motivo Appuntamento</p>
              <p className="text-sm text-slate-700">{ap.motivo}</p>
            </div>
            {isCancellato && ap.cancellazione_motivo && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mt-3">
                <p className="text-xs text-red-800 font-semibold mb-1">Motivo Cancellazione</p>
                <p className="text-sm text-red-900">{ap.cancellazione_motivo}</p>
              </div>
            )}
          </div>

          {/* Azioni */}
          {!isCancellato && (
            <div className="border-t border-slate-200 p-6 bg-slate-50">
              {!showCancella && !showRipro && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700 mb-3">
                    Se non puoi essere presente, avvisaci telefonicamente allo{" "}
                    <a href="tel:+390702043952" className="font-semibold text-blue-700 hover:underline">
                      070 204 3952
                    </a>{" "}
                    oppure via email a{" "}
                    <a href="mailto:info@showroomingenio.it" className="font-semibold text-blue-700 hover:underline">
                      info@showroomingenio.it
                    </a>
                    . In alternativa gestisci qui:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      onClick={() => setShowRipro(true)}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="btn-show-ripro"
                    >
                      <CalendarClock className="w-4 h-4 mr-2" />
                      Riprogramma
                    </Button>
                    <Button
                      onClick={() => setShowCancella(true)}
                      variant="destructive"
                      data-testid="btn-show-cancella"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancella Appuntamento
                    </Button>
                  </div>
                </div>
              )}

              {showCancella && (
                <div className="space-y-3 bg-red-50 border-2 border-red-300 rounded-xl p-4">
                  <div className="flex items-start gap-2 mb-1">
                    <XCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-red-900">
                        Prima di confermare, indichi il motivo della cancellazione
                      </p>
                      <p className="text-xs text-red-800 mt-0.5">
                        Questa informazione ci aiuta a migliorare il servizio. Il testo verrà inviato al nostro team.
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="motivo-cancel" className="text-base font-bold text-red-900 flex items-center gap-1">
                      📝 Motivo della Cancellazione <span className="text-red-600">*</span>
                    </Label>
                    <Textarea
                      id="motivo-cancel"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Es. Sopraggiunta impossibilità, rimando a nuova data, altro impegno improvviso..."
                      rows={5}
                      className="mt-2 border-2 border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 text-base bg-white"
                      data-testid="input-motivo-cancel"
                      autoFocus
                    />
                    {!motivo.trim() && (
                      <p className="text-xs text-red-700 mt-1 font-medium">
                        ⚠ Campo obbligatorio: scrivi almeno una breve motivazione per procedere
                      </p>
                    )}
                    {motivo.trim() && (
                      <p className="text-xs text-emerald-700 mt-1 font-medium">
                        ✓ Motivo inserito ({motivo.trim().length} caratteri)
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <Button
                      onClick={handleCancella}
                      disabled={cancelling || !motivo.trim()}
                      variant="destructive"
                      className="flex-1"
                      data-testid="btn-confirm-cancella"
                    >
                      {cancelling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cancellazione...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Conferma Cancellazione
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowCancella(false);
                        setMotivo("");
                      }}
                      variant="outline"
                      className="bg-transparent"
                    >
                      Annulla
                    </Button>
                  </div>
                </div>
              )}

              {showRipro && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="new-date" className="text-sm font-semibold">
                      Nuova Data
                    </Label>
                    <Input
                      id="new-date"
                      type="date"
                      value={newDate}
                      min={todayIso()}
                      onChange={(e) => {
                        setNewDate(e.target.value);
                        setSelectedSlot("");
                      }}
                      data-testid="input-new-date"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Nuovo Orario</Label>
                    {loadingSlots ? (
                      <p className="text-sm text-slate-500 py-2">Caricamento slot...</p>
                    ) : slots.length === 0 ? (
                      <p className="text-sm text-amber-700 py-2">Nessuno slot libero in questa data. Prova un'altra.</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-1.5 mt-1 max-h-[168px] overflow-y-auto p-1 border rounded">
                        {slots.map((s) => (
                          <button
                            key={s.orario}
                            type="button"
                            onClick={() => setSelectedSlot(s.orario)}
                            data-testid={`ripro-slot-${s.orario}`}
                            className={`bg-transparent text-xs px-2 py-1.5 rounded font-medium border transition-colors ${
                              selectedSlot === s.orario
                                ? "bg-amber-600 text-white border-amber-700"
                                : "bg-white text-slate-800 border-slate-300 hover:bg-amber-50"
                            }`}
                          >
                            {s.orario}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={handleRiprogramma}
                      disabled={rescheduling || !selectedSlot}
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      data-testid="btn-confirm-ripro"
                    >
                      {rescheduling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Aggiornamento...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Conferma Nuovo Orario
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowRipro(false);
                        setSelectedSlot("");
                      }}
                      variant="outline"
                      className="bg-transparent"
                    >
                      Annulla
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-slate-500 text-center mt-4">
          Showroom Ingenio · Cagliari · 070 204 3952 · info@showroomingenio.it
        </p>
      </div>
    </div>
  );
}
